import type { GraphNode as NodeType, Socket } from "~/js/graph/node-socket-connection";
import { findSocket } from "~/solidJs/shared/helpers/nodeHelpers";
import type { IModifierRecord } from "~/solidJs/shared/helpers/types/INodeDefinition";
import { styleMethods } from "~/solidJs/widgets/NodeStyleWidget/NodeStyleWidget";
import EventAnalytics from "../EventAnalytics";

const PlussIcon = `<svg class="size-2" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
<line x1="9.30762" y1="1" x2="9.30762" y2="17" stroke="black" stroke-width="2" stroke-linecap="round"/>
<line x1="17" y1="9.30762" x2="1" y2="9.30762" stroke="black" stroke-width="2" stroke-linecap="round"/>
</svg>
`;
const ArrowIcon = `<svg class="size-2 rotate-180" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.0519 2.88508C16.5619 2.39508 15.7719 2.39508 15.2819 2.88508L6.87188 11.2951C6.48188 11.6851 6.48188 12.3151 6.87188 12.7051L15.2819 21.1151C15.7719 21.6051 16.5619 21.6051 17.0519 21.1151C17.5419 20.6251 17.5419 19.8351 17.0519 19.3451L9.71188 12.0051L17.0619 4.65508C17.5419 4.16508 17.5419 3.37508 17.0519 2.88508Z"
          fill="#0E0F11"/>
</svg>

`;
/**
 * style contains in it unput and params to modify, displayed name and unique value
 */
type Style = {
	inputs?: IModifierRecord;
	params?: IModifierRecord;
	name: string;
	/**
	 * unique identifier of a style
	 */
	value: string;
};

/**
 * style data used to transfer between widget and node param and in saved workspace config
 */
export type StyleData = {
	style: Style;
};

/**
 * custom user selected lora model or checkpoint
 */
type Model = {
	inputs: IModifierRecord;
	params: IModifierRecord;
	name: string;
	/**
	 * unique identifier of a model
	 */
	value: string;
};

/**
 * advanced data used to transfer between widget and node param and in saved workspace config data about user custom selected models
 */
type AdvancedData = {
	loras: Model[];
	checkpoints: Model[];
};

/**
 * user can select predefined style or custom pack of loras && checkpoints
 */
type ParamData = StyleData | AdvancedData;

type StyleParamProps = {
	socket: Socket;
	node: NodeType;
	optionalInfo: { value?: ParamData };
};

const isParamInStyleData = (paramName: string, data?: Style): boolean => {
	if (!data) return false;
	return !!data?.params?.[paramName];
};

const ParamsToResetBack = ["lora_model_3", "lora_model_2", "lora_model_1", "link_to_model"];

export class StyleParam {
	socket: Socket;
	node: NodeType;
	optionalInfo: { value?: ParamData | null };
	dom!: {
		domElement: HTMLButtonElement;
		title: HTMLDivElement;
		icon: HTMLDivElement;
		styleBtn?: {
			btn: HTMLButtonElement;
			txt: HTMLDivElement;
		};
	};

	constructor(props: StyleParamProps) {
		this.socket = props.socket;
		this.node = props.node;
		this.optionalInfo = props.optionalInfo;

		this.initDom();
		this.restoreState();
		if (styleMethods.isNeedToOpenFirstInSessionStyles(props.node)) {
			this.handleOpenWidget();
		}
	}
	setCustomSerialize() {
		this.socket.customSerializationData = () => {
			return {};
		};
	}
	restoreState() {
		const info = this.optionalInfo;
		const wasEnabled = this.socket.getIsModificationEnabled();
		setTimeout(() => {
			if (!info.value) {
				return;
			}
			this.setValue(info.value, wasEnabled);
		}, 0);
	}
	createIcon(icon: string) {
		const el = document.createElement("div");
		el.innerHTML = icon; // Assuming `iconSvg` contains the SVG content as a string"
		el.className = "absolute right-4 top-1/2 -translate-y-1/2";
		return el;
	}
	updateIcon(icon: string) {
		this.dom.icon.innerHTML = icon;
	}
	initDom() {
		this.dom = {
			domElement: document.createElement("button"),
			title: document.createElement("div"),
			icon: this.createIcon(PlussIcon)
		};
		this.dom.domElement.addEventListener("click", this.handleOpenWidget.bind(this));
		this.dom.domElement.className =
			"w-full text-sm gap-1 p-3 flex relative justify-start items-center rounded-xl border border-purple-500";
		this.dom.title.className = "text-text-primary";
		this.dom.title.textContent = "Add style";
		this.dom.domElement.append(this.dom.title);
		this.dom.domElement.append(this.dom.icon);
	}
	handleOpenWidget() {
		this.node.isStyleOpen = true;
		EventAnalytics.styles.open(this.node.globalId);
		styleMethods.addStyleItem({ nodeUUID: this.node.uuid, globalId: this.node.globalId });
	}
	setValue(data: ParamData | null, isModificationEnabled?: boolean) {
		if (!data) {
			this.destroyStyleBtn();
			this.optionalInfo.value = data;
			return;
		}
		this.optionalInfo.value = data;
		this.render(data);
		if (isStyle(data)) {
			this.socket.modifySockets(data.style.params || {}, isModificationEnabled);
			this.socket.modifySockets(data.style.inputs || {}, isModificationEnabled);
		}
	}
	getValue() {
		return this.optionalInfo.value;
	}
	getDomElement() {
		return this.dom.domElement;
	}

	render(data: ParamData) {
		if (isStyle(data)) {
			this.renderStyle(data);
			return;
		}
	}

	getStyleBtn(data: StyleData) {
		const deleteIcon = document.createElement("button");
		const btn = document.createElement("button");
		const txt = document.createElement("div");
		txt.textContent = data.style.name;
		btn.append(txt, deleteIcon);
		btn.className =
			"border-none text-text-secondary rounded-lg p-1 text-2xs flex gap-2 items-center justify-center bg-gray-200";
		deleteIcon.className =
			"border-none hover:bg-gray-300 p-0 aspect-square text-[8px] rounded-md close-icon";
		deleteIcon.addEventListener("click", (e) => {
			e.stopPropagation();
			this.setValue(null);
		});
		return { btn, txt };
	}

	updateStyleBtn(data: StyleData) {
		if (!this.dom.styleBtn) {
			return;
		}
		this.dom.styleBtn.txt.textContent = data.style.name;
	}
	destroyStyleBtn() {
		if (!this.dom.styleBtn) {
			return;
		}
		this.node.closeStyleWidget();
		this.updateIcon(PlussIcon);
		this.dom.styleBtn.btn.remove();
		this.removeStyleInputModifiers();
		this.dom.styleBtn = undefined;
		this.dom.title.textContent = "Add style";
	}
	removeStyleInputModifiers() {
		if (this.optionalInfo?.value && isStyle(this.optionalInfo.value)) {
			for (const paramName of ParamsToResetBack) {
				if (isParamInStyleData(paramName, this.optionalInfo.value.style)) {
					const socket = findSocket(paramName, this.node);
					if (socket) {
						socket.setValue("");
					}
				}
			}
		}
		this.node.inputSocketGroupModifiers.deleteAllSocketModifications(this.socket.name);
	}
	renderStyle(data: StyleData) {
		if (this.dom.styleBtn) {
			this.updateStyleBtn(data);
			return;
		}
		this.updateIcon(ArrowIcon);
		this.dom.title.textContent = "Style";
		this.dom.styleBtn = this.getStyleBtn(data);
		this.getDomElement().append(this.dom.styleBtn.btn);
	}
}

const isStyle = (data: ParamData): data is StyleData => {
	return "style" in data;
};
